import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, Bell, LogOut, ChevronDown } from "lucide-react";
import Logo, { Emblem } from "@/brand/Logo";
import Icon from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { MODULES, GROUPS, ROLE_LABELS } from "@/pages/portal/portalConfig";

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => { setOpen(false); setMenu(false); }, [location.pathname]);

  const doLogout = () => { logout(); navigate("/portal/login", { replace: true }); };
  const current = MODULES.find((m) => location.pathname === `/portal/${m.slug}` || (m.slug === "overview" && location.pathname === "/portal"));
  const pageTitle = location.pathname === "/portal/admin" ? "Content Editor" : (current?.label || "Overview");

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? "bg-cbblue-soft text-primary" : "text-cbslate hover:bg-cbmist hover:text-cbink"
    }`;

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-[64px] items-center gap-2 border-b border-border px-5">
        <Link to="/portal"><Logo size={30} /></Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Portal">
        {GROUPS.map((g) => {
          const items = MODULES.filter((m) => m.group === g.key);
          if (!items.length) return null;
          return (
            <div key={g.key} className="mb-4">
              {g.label && <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-cbslate/70">{g.label}</p>}
              <div className="space-y-0.5">
                {items.map((m) => (
                  <NavLink key={m.slug} to={m.slug === "overview" ? "/portal" : `/portal/${m.slug}`} end={m.slug === "overview"} className={linkClass} data-testid={`portal-nav-${m.slug}`}>
                    <Icon name={m.icon} size={18} /> {m.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
        <div className="mb-4">
          <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-cbslate/70">Billing</p>
          <NavLink to="/portal/billing" className={linkClass} data-testid="portal-nav-billing">
            <Icon name="CreditCard" size={18} /> Billing &amp; Payments
          </NavLink>
        </div>
        {user?.is_staff && (
          <div className="mb-4">
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-cbslate/70">Admin</p>
            <NavLink to="/portal/admin" className={linkClass} data-testid="portal-nav-admin">
              <Icon name="FileText" size={18} /> Content Editor
            </NavLink>
            <Link to="/cbconsole" className="mt-0.5 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-cbslate transition-colors hover:bg-cbmist hover:text-cbink" data-testid="portal-nav-cbconsole">
              <Icon name="Building2" size={18} /> Clients Console
            </Link>
          </div>
        )}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-cbmist">
      {/* sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-border bg-white lg:block">{Sidebar}</aside>
      {/* sidebar mobile */}
      {open && <div className="fixed inset-0 z-40 bg-cbink/40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-white transition-transform lg:hidden ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-cbslate" aria-label="Close"><X size={20} /></button>
        {Sidebar}
      </aside>

      <div className="lg:pl-64">
        {/* topbar */}
        <header className="sticky top-0 z-30 flex h-[64px] items-center justify-between border-b border-border bg-white px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu"><Menu size={22} /></button>
            <div>
              <h1 className="font-display text-lg font-bold text-cbink">{pageTitle}</h1>
              <p className="text-xs text-cbslate">{user?.tenant_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NavLink to="/portal/notifications" className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-border text-cbslate hover:text-primary" aria-label="Notifications">
              <Bell size={18} />
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary" />
            </NavLink>
            <div className="relative">
              <button onClick={() => setMenu((m) => !m)} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-sm" data-testid="portal-usermenu">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-cbblue-soft text-xs font-bold text-primary">{(user?.name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                <span className="hidden text-left sm:block"><span className="block text-xs font-semibold text-cbink">{user?.name}</span><span className="block text-[11px] text-cbslate">{ROLE_LABELS[user?.role] || user?.role}</span></span>
                <ChevronDown size={15} className="text-cbslate" />
              </button>
              {menu && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-white p-2 shadow-panel">
                  <div className="border-b border-border px-3 py-2"><p className="text-sm font-semibold text-cbink">{user?.email}</p><p className="text-xs text-cbslate">{ROLE_LABELS[user?.role]}</p></div>
                  <Link to="/portal/account-settings" className="mt-1 block rounded-lg px-3 py-2 text-sm text-cbslate hover:bg-cbmist">Account settings</Link>
                  <button onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-destructive hover:bg-red-50" data-testid="portal-logout"><LogOut size={16} /> Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="px-4 py-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800">
            Demonstration environment — all portal data shown is sample data for illustration.
          </div>
        </div>

        <main className="px-4 pb-16 pt-2 sm:px-6"><Outlet /></main>
      </div>
    </div>
  );
}
